<script setup>
import { computed, inject, ref } from 'vue';
import { pushToDataLayer } from '@/ui/scripts/analytics/analytics.methods';

const { listView } = inject('config');
const props = defineProps({
  project: {
    required: true,
    type: Object,
  },
});
const eventsVisible = ref(false);
const hasEvents = computed(() => props.project.events !== null && props.project.events.length > 0);
const ariaControls = computed(() => {
  return props.project.events.map((_event, index) => `${props.project.uid}--${index}`).join(' ');
});

function toggleEvents() {
  eventsVisible.value = !eventsVisible.value;
}

function getDestinationUrl(prop) {
  const isToggleBtn = typeof prop === 'boolean';

  if (isToggleBtn) {
    return prop ? 'Open' : 'Close';
  }
  return prop;
}

function gtmProjectListCtaTrigger(url, project, cta) {
  const ctaSelected = cta ? `- ${cta}` : '';

  pushToDataLayer(
    'projectMapProjectListCTA_tracking',
    'projectMapProjectListCTA_action',
    'projectMapProjectListCTA_label',
    `Click - CTA - ${project} ${ctaSelected}`,
    getDestinationUrl(url),
  );
}
</script>

<template>
  <tr class="c-projects-table__row">
    <td :data-heading="listView.projectHeading" class="c-projects-table__cell">
      <a
        @click="gtmProjectListCtaTrigger(props.project.url, props.project.name, '')"
        :href="props.project.url"
        class="c-projects-table__link"
        >{{ props.project.name }}</a
      >
    </td>
    <td :data-heading="listView.typeHeading" class="c-projects-table__cell">
      {{ props.project.type }}
    </td>
    <td :data-heading="listView.statusHeading" class="c-projects-table__cell">
      {{ props.project.status }}
    </td>
    <td class="c-projects-table__cell">
      <button
        v-if="hasEvents"
        @click="
          toggleEvents();
          gtmProjectListCtaTrigger(eventsVisible, props.project.name, 'Related events');
        "
        :aria-controls="ariaControls"
        :aria-expanded="eventsVisible"
        class="c-projects-table__button"
        type="button"
      >
        Related events
        <svg focusable="false" aria-hidden="true">
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#down-arrow"></use>
        </svg>
      </button>
    </td>
  </tr>
  <template v-if="eventsVisible && hasEvents">
    <tr
      v-for="(event, eventIndex) in props.project.events"
      :id="`${props.project.uid}--${eventIndex}`"
      :key="event.id"
    >
      <td class="c-projects-table__cell c-projects-table__cell--event">
        <a
          @click="gtmProjectListCtaTrigger(event.url, props.project.name, event.name)"
          :href="event.url"
          class="c-projects-table__link"
          >{{ event.name }}</a
        >
      </td>
      <td class="c-projects-table__cell c-projects-table__cell--event" colspan="3">
        {{ event.desc }}
      </td>
    </tr>
  </template>
</template>

<style lang="scss">
@import 'sass-mq';
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/mixins/buttons';
@import '@/ui/styles/mixins/focus';
@import '@/ui/styles/settings/breakpoints';
@import '@/ui/styles/settings/colors';
@import '@/ui/styles/settings/fonts';
@import '@/ui/styles/settings/units';

.c-projects-table__row:not(:first-child) {
  border-top: $border-width-thin solid $ssen-dark-grey;
  @include mq($until: large) {
    display: block;
    margin-top: rem(8px);
  }
}

.c-projects-table__cell {
  @include mq($until: large) {
    display: block;
    padding: $spacing-x-small 0;

    &[data-heading]::before {
      content: attr(data-heading);
      display: block;
      padding-bottom: $spacing-small;
      color: $color-brand-two;
      font-weight: $font-weight-bold;
    }

    &:first-child:not(.c-projects-table__cell--event) {
      margin-top: rem(23px);
    }

    &:last-child:not(.c-projects-table__cell--event) {
      margin-bottom: $spacing-x-large;
    }

    &:last-child.c-projects-table__cell--event {
      margin-bottom: rem(21px);
    }
  }

  @include mq($from: large) {
    display: table-cell;
    padding: rem(13px) $spacing-large;
  }
}

.c-projects-table__link {
  color: $color-brand-one;
  font-weight: $font-weight-semi-bold;
  text-decoration: underline;
}

.c-projects-table__button {
  @include iconButton(rem(15px));
  @include removeDefaultButtonStyling();
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: map-get($color-grey, 1);
  text-decoration: underline;
  cursor: pointer;

  & > svg {
    margin-left: $spacing-small;
    color: $color-brand-one;
  }

  &[aria-expanded='true'] > svg {
    transform: rotate(180deg);
  }

  @include mq($until: large) {
    margin-top: rem(21px);
    width: auto;
  }

  @include mq($from: large) {
    margin-top: 0;
    width: 100%;
  }
}
</style>
